import React from 'react';
import type { BrochureAttachment } from '~/lib/pdf/brochure';
import { classNames } from '~/utils/classNames';

interface BrochurePreviewProps {
  brochure: BrochureAttachment | null;
  onRemove: () => void;
}

function statusLine(brochure: BrochureAttachment): { text: string; tone: 'muted' | 'accent' | 'danger' } {
  switch (brochure.status) {
    case 'processing': {
      const { progress } = brochure;

      if (progress?.phase === 'rendering' && progress.page) {
        return { text: `Rendering page ${progress.page}/${progress.total}…`, tone: 'accent' };
      }

      if (progress?.phase === 'extracting') {
        return { text: 'Extracting photos…', tone: 'accent' };
      }

      return { text: 'Reading PDF…', tone: 'accent' };
    }
    case 'ready': {
      const { artifacts } = brochure;
      const photos = artifacts?.embeddedImages.length ?? 0;

      return {
        text: `Ready — ${artifacts?.pageCount ?? '?'} pages${photos > 0 ? `, ${photos} photo${photos === 1 ? '' : 's'}` : ''}`,
        tone: 'muted',
      };
    }
    case 'uploading':
      return { text: 'Uploading brochure…', tone: 'accent' };
    case 'error':
    default:
      return { text: brochure.error ?? "Couldn't read this PDF", tone: 'danger' };
  }
}

const BrochurePreview: React.FC<BrochurePreviewProps> = ({ brochure, onRemove }) => {
  if (!brochure) {
    return null;
  }

  const status = statusLine(brochure);
  const busy = brochure.status === 'processing' || brochure.status === 'uploading';

  // Compact ChatGPT/Claude-style attachment chip. Lives inside the composer
  // (see ChatBox attachments row) — no card chrome, floating remove button,
  // processing shown as a spinner overlay on the thumbnail.
  return (
    <div className="group relative flex items-center gap-2.5 w-[218px] p-2 pr-6 bg-dunea-elements-background-depth-3 border border-dunea-elements-borderColor rounded-lg">
      <div className="relative flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-sunken border border-hairline">
        {brochure.artifacts?.page1Thumb ? (
          <img src={brochure.artifacts.page1Thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="i-ph:file-pdf text-[16px] text-muted" />
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="i-ph:spinner-gap text-[14px] text-white ui-spin" />
          </div>
        )}
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        {/* Brochure mode must be unmistakable — this file drives the whole build. */}
        <span className="flex items-center gap-1 font-geist text-[10px] font-medium uppercase tracking-wide text-accent">
          <span className="i-ph:magic-wand text-[11px]" />
          Brochure · building your site
        </span>
        <span className="font-geist text-[12px] text-primary truncate" title={brochure.file.name}>
          {brochure.file.name}
        </span>
        <span
          className={classNames(
            'font-geist text-[11px] truncate',
            status.tone === 'accent' && 'text-accent',
            status.tone === 'danger' && 'text-danger',
            status.tone === 'muted' && 'text-muted',
          )}
        >
          {status.text}
        </span>
      </div>

      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black border border-hairline flex items-center justify-center hover:bg-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed touch:w-7 touch:h-7 touch:top-0.5 touch:right-0.5"
        aria-label="Remove brochure"
      >
        <span className="i-ph:x w-3 h-3 text-gray-200" />
      </button>
    </div>
  );
};

export default BrochurePreview;
